import { useState } from 'react';
import { Hand } from 'lucide-react';
import Button from '@/components/primitives/Button.jsx';
import Icon from '@/components/primitives/Icon.jsx';
import ExecutionStatus from '@/components/surfaces/ExecutionStatus.jsx';
import toastManager from '@/lib/toast/toastManager.js';
import { respondApproval } from '@/redux/requests/approvalRequests.js';
import toApprovalActionLines from '../lib/toApprovalActionLines.js';
import toApprovalDecision from '../lib/toApprovalDecision.js';

/**
 * Pieve asks before an action that changes something. The card says what it wants to do and where,
 * and offers allow once, allow from now on (when the action allows it) and deny. Once answered it
 * keeps the decision on record.
 */
const ApprovalCard = ({ approval, threadId, isOpen }) => {
  const [answering, setAnswering] = useState('');
  const actionLines = toApprovalActionLines(approval.actions);
  const decision = toApprovalDecision(approval.decision);
  const decided = Boolean(decision);
  const showDetail = approval.detail && !actionLines.some((line) => line.label === approval.detail);

  const answer = async ({ approved, remember, key }) => {
    setAnswering(key);
    const res = await respondApproval({ approvalId: approval.approvalId, approved, remember, threadId, runId: approval.runId, responseMode: approval.responseMode });
    setAnswering('');
    if (res?.success === false) {
      toastManager.add({ title: 'Your answer was not sent', description: res.message || 'Try again.', type: 'error' });
    }
  };

  return (
    <section className="assistant-approval" data-state={decided ? decision.state : isOpen ? 'open' : 'closed'} aria-label="Approval">
      <header className="assistant-approval__head">
        <span className="assistant-approval__mark">
          <Icon icon={Hand} size="md" />
        </span>
        <div className="assistant-approval__heading">
          <h3 className="assistant-approval__title">{approval.title}</h3>
          {showDetail && <p className="assistant-approval__detail">{approval.detail}</p>}
        </div>
        {decided ? <ExecutionStatus state={decision.status} size="sm" detail={decision.label} /> : isOpen ? <ExecutionStatus state="waiting" size="sm" /> : null}
      </header>
      {actionLines.length > 0 && (
        <ul className="assistant-approval__actions" aria-label="What Pieve will do">
          {actionLines.map((line) => (
            <li key={line.key} className="assistant-approval__action">
              <span className="assistant-approval__action-label">{line.label}</span>
              {line.values.map((value) => (
                <code key={value} className="assistant-approval__action-value">
                  {value}
                </code>
              ))}
            </li>
          ))}
        </ul>
      )}
      {!decided && isOpen && (
        <div className="assistant-approval__buttons">
          <Button variant="primary" loading={answering === 'once'} disabled={Boolean(answering)} onClick={() => answer({ approved: true, remember: false, key: 'once' })}>
            {approval.approveOnceLabel}
          </Button>
          {approval.rememberEnabled && (
            <Button variant="secondary" loading={answering === 'remember'} disabled={Boolean(answering)} onClick={() => answer({ approved: true, remember: true, key: 'remember' })}>
              {approval.approveRememberLabel}
            </Button>
          )}
          <Button variant="ghost" loading={answering === 'deny'} disabled={Boolean(answering)} onClick={() => answer({ approved: false, remember: false, key: 'deny' })}>
            {approval.denyLabel}
          </Button>
        </div>
      )}
    </section>
  );
};

export default ApprovalCard;
